import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
} from "@mui/material";
import StatusChip from "../common/StatusChip";
import { PurchaseOrderResponseDto } from "../../shared/dtos/purchaseOrder/PurchaseOrderResponseDto";
import { formatPlainNumber } from "../../utils/appUtils";
import { castEnumForDisplay } from "../../helpers/enumHelpers";

interface PurchaseOrderDetailsTabProps {
  order: PurchaseOrderResponseDto;
}

const PurchaseOrderDetailsTab: React.FC<PurchaseOrderDetailsTabProps> = ({
  order,
}) => {
  const { items, payments } = order;
  const currency = "KES";

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Stack spacing={3}>
          <Card
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider" }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Supplier Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Supplier Name
              </Typography>
              <Typography
                variant="body1"
                gutterBottom
                sx={{ fontWeight: "500" }}
              >
                {order.supplierName || "N/A"}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                sx={{ mt: 2 }}
              >
                Expected Delivery Date
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "500" }}>
                {order.expectedDeliveryDate
                  ? new Date(order.expectedDeliveryDate).toLocaleDateString()
                  : "Not Confirmed"}
              </Typography>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider" }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Branch Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                {order.branchName || "Main Campus"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Created By: {order.createdByUserName || "Staff"}
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, md: 8 }}>
        <Card
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider", mb: 3 }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
              Purchase Order Items
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: "background.default" }}>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="center">Ordered</TableCell>
                    <TableCell align="center">Received</TableCell>
                    <TableCell align="right">Unit Cost</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items?.map((item) => {
                    const ordered = Number(item.orderedQuantity || 0);
                    const received = Number(item.receivedQuantity || 0);
                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            {item.productName || "Unnamed Item"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.category || ""}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{ordered}</TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            color:
                              received < ordered ? "warning.main" : "inherit",
                          }}
                        >
                          {received}
                        </TableCell>
                        <TableCell align="right">
                          {formatPlainNumber(item.unitCost)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatPlainNumber(item.totalCost)}
                        </TableCell>
                        <TableCell align="center">
                          <StatusChip status={item.status} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Payment History
                </Typography>
                {payments && payments.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {payments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell sx={{ borderBottom: "none", pl: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                {castEnumForDisplay(payment.paymentMethod)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                {new Date(payment.createdAt).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                borderBottom: "none",
                                pr: 0,
                                fontWeight: "bold",
                                color: "success.main",
                              }}
                            >
                              {formatPlainNumber(payment.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No payments recorded yet.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Summary
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    mt: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Currency
                  </Typography>
                  <Typography
                    variant="body2"
                    color="secondary.main"
                    sx={{ fontWeight: "bold" }}
                  >
                    {currency}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                    mt: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Order Total
                  </Typography>
                  <Typography
                    variant="h6"
                    color="secondary.main"
                    sx={{ fontWeight: "bold" }}
                  >
                    {currency} {formatPlainNumber(order.total)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Total Paid
                  </Typography>
                  <Typography
                    variant="body2"
                    color="success.main"
                    sx={{ fontWeight: "bold" }}
                  >
                    {formatPlainNumber(order.paidAmount)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    Pending Balance
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      color:
                        Number(order.balanceAmount || 0) > 0
                          ? "error.main"
                          : "secondary.main",
                    }}
                  >
                    {formatPlainNumber(order.balanceAmount)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PurchaseOrderDetailsTab;
